import { debounce, requestIdleCallback, viewTransition } from '@fc/utilities';

const FIT_FLOAT_PANEL_TAG = 'float-panel';
const FIT_FLOAT_PANEL_OFFSET_PX = 40;
const FIT_FLOAT_PANEL_OBSERVED_ATTRIBUTES = ['class', 'style', 'open'];
const FIT_FLOAT_PANEL_RESIZE_DEBOUNCE_MS = 100;

export class FloatingPanelComponent extends HTMLElement {
  #abortController = null;
  #mutationObserver = null;

  #setVerticalOffset() {
    this.style.top = `${FIT_FLOAT_PANEL_OFFSET_PX}px`;
  }

  #resolveHorizontalOffset(rect, viewportWidth) {
    if (rect.right > viewportWidth) {
      return `-${rect.right - viewportWidth + FIT_FLOAT_PANEL_OFFSET_PX}px`;
    }

    if (rect.left < 0) {
      return `${Math.abs(rect.left) + FIT_FLOAT_PANEL_OFFSET_PX}px`;
    }

    return null;
  }

  #applyHorizontalOffset(rect, viewportWidth) {
    const fitLeftOffset = this.#resolveHorizontalOffset(rect, viewportWidth);
    if (fitLeftOffset === null) {
      this.style.removeProperty('left');
      return;
    }
    this.style.left = fitLeftOffset;
  }

  #closeParentDetailsOnResize() {
    const fitParentDetails = this.closest('details');
    const fitCloseOnResize = this.dataset.closeOnResize === 'true';
    if (!(fitParentDetails instanceof HTMLDetailsElement) || !fitCloseOnResize) return;
    fitParentDetails.open = false;
    fitParentDetails.removeAttribute('open');
  }

  #reposition = async () => {
    if (viewTransition?.current) await viewTransition.current;

    const fitRect = this.getBoundingClientRect();
    const fitViewportWidth = window.innerWidth;
    if (!fitRect || fitViewportWidth <= 0) return;

    this.#setVerticalOffset();
    this.#applyHorizontalOffset(fitRect, fitViewportWidth);
    this.#mutationObserver?.takeRecords();
  };

  #onResize = debounce(() => {
    this.#closeParentDetailsOnResize();
    this.#reposition();
  }, FIT_FLOAT_PANEL_RESIZE_DEBOUNCE_MS);

  connectedCallback() {
    this.#abortController = new AbortController();
    window.addEventListener('resize', this.#onResize, {
      signal: this.#abortController.signal,
      passive: true,
    });

    this.#mutationObserver = new MutationObserver(() => this.#reposition());

    requestIdleCallback(() => {
      this.#reposition();
      this.#mutationObserver?.observe(this, {
        attributes: true,
        attributeFilter: FIT_FLOAT_PANEL_OBSERVED_ATTRIBUTES,
      });
    });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#mutationObserver?.disconnect();
    this.#mutationObserver = null;
  }
}

function registerFitFloatingPanelElement() {
  if (customElements.get(FIT_FLOAT_PANEL_TAG)) return;
  customElements.define(FIT_FLOAT_PANEL_TAG, FloatingPanelComponent);
}

registerFitFloatingPanelElement();
